import { useId } from 'react'
import { cn } from 'tailwind-v4/lib/utils'

/**
 * The voice orb.
 *
 * INLINE SVG, not an `<img>`, for two reasons. It picks the brand ramp up
 * from `globals.css` as real custom properties, so the orb follows the theme
 * rather than carrying its own copy of three hex values. And it can be
 * driven by `level`, which is the whole point.
 *
 * IT REACTS TO REAL AUDIO, never to a timer. The level comes from the Web
 * Audio analyser the voice hook already builds: the microphone's while the
 * person speaks, the playback's while the agent does. A pulse on a timer
 * looks convincing for about four seconds and then reads as a loading
 * spinner, which is exactly wrong here, because the orb is the only
 * feedback that the microphone is actually open.
 *
 * WHO IS TALKING CHANGES HOW IT MOVES, not just how fast. Listening, it
 * breathes and the colour fields drift slowly: it is waiting. Speaking, the
 * swirl quickens and the ripples go out: it is producing. Same object, two
 * legible behaviours, so somebody can tell at a glance whether it is their
 * turn without reading a word.
 *
 * Every id is scoped with `useId`. Two orbs on one page sharing a gradient
 * id is the classic inline-SVG bug: the second one silently steals the
 * first's paint.
 */

export type OrbState = 'idle' | 'listening' | 'thinking' | 'speaking'

export function VoiceOrb({
  state,
  /** 0..1 from the live analyser. Ignored when idle. */
  level = 0,
  className,
}: {
  state: OrbState
  level?: number
  className?: string
}) {
  const uid = useId().replace(/:/g, '')
  const id = (name: string) => `${name}-${uid}`

  const live = state === 'listening' || state === 'speaking'
  // Loudness moves the body. Capped well under the halo so a shout cannot
  // push the sphere outside its own glow.
  // Rounded: `1 + 1 * 0.14` is 1.1400000000000001 in binary floating point,
  // and three decimals is finer than a pixel at this size anyway.
  const scale = live
    ? Math.round((1 + Math.min(level, 1) * 0.14) * 1000) / 1000
    : 1
  // Speaking is quick and busy; listening is slow and patient.
  const swirl = state === 'speaking' ? 3.5 : 9
  const morph = state === 'speaking' ? 0.7 : 2.4

  return (
    <svg
      viewBox="0 0 200 200"
      className={cn('block', className)}
      role="img"
      aria-label={
        state === 'speaking'
          ? 'The assistant is speaking'
          : state === 'listening'
            ? 'Listening'
            : state === 'thinking'
              ? 'Working on it'
              : 'Voice'
      }
      data-state={state}
    >
      <defs>
        <linearGradient id={id('brand')} x1="0.15" y1="0.1" x2="0.85" y2="0.9">
          <stop
            offset="0%"
            style={{ stopColor: 'var(--brand-hot, #ff2d87)' }}
          />
          <stop
            offset="55%"
            style={{ stopColor: 'var(--brand-warm, #ff6b4a)' }}
          />
          <stop
            offset="100%"
            style={{ stopColor: 'var(--brand-glow, #ffa234)' }}
          />
        </linearGradient>

        {/* Darkens the rim, which is what makes it read as a sphere. */}
        <radialGradient id={id('depth')} cx="0.42" cy="0.38" r="0.62">
          <stop offset="60%" stopColor="#000" stopOpacity="0" />
          <stop offset="100%" stopColor="#5a0a2a" stopOpacity="0.35" />
        </radialGradient>

        <radialGradient id={id('shine')} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.75" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>

        <filter id={id('soft')} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="9" />
        </filter>
        <filter id={id('halo')} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="12" />
        </filter>

        <clipPath id={id('clip')}>
          <circle cx="100" cy="100" r="72" />
        </clipPath>
      </defs>

      <style>{`
        [data-state] .orb-swirl     { transform-origin: 100px 100px; animation: orb-spin ${String(swirl)}s linear infinite; }
        [data-state] .orb-swirl-rev { transform-origin: 100px 100px; animation: orb-spin ${String(swirl * 1.6)}s linear infinite reverse; }
        [data-state] .orb-blob      { transform-box: fill-box; transform-origin: center; }
        [data-state] .blob-a { animation: orb-morph ${String(morph)}s ease-in-out infinite; }
        [data-state] .blob-b { animation: orb-morph ${String(morph * 1.3)}s ease-in-out infinite .3s; }
        [data-state] .blob-c { animation: orb-morph ${String(morph * 0.85)}s ease-in-out infinite .6s; }
        [data-state] .orb-ring { transform-origin: 100px 100px; animation: orb-ripple 2.4s ease-out infinite; opacity: 0; }
        [data-state] .orb-ring.r2 { animation-delay: .8s; }
        [data-state] .orb-ring.r3 { animation-delay: 1.6s; }
        @keyframes orb-spin   { to { transform: rotate(360deg); } }
        @keyframes orb-morph  { 0%,100% { transform: scale(1,1); } 33% { transform: scale(1.25,.85); } 66% { transform: scale(.85,1.2); } }
        @keyframes orb-ripple { 0% { opacity: .55; transform: scale(.82); } 100% { opacity: 0; transform: scale(1.3); } }
        @media (prefers-reduced-motion: reduce) {
          [data-state] .orb-swirl, [data-state] .orb-swirl-rev,
          [data-state] .orb-blob, [data-state] .orb-ring { animation: none; }
        }
      `}</style>

      {/* Ripples only while it is TALKING: an expanding ring means output,
          and showing it while listening would say the wrong thing. */}
      {state === 'speaking' ? (
        <g fill="none" stroke={`url(#${id('brand')})`} strokeWidth="1.5">
          <circle className="orb-ring" cx="100" cy="100" r="74" />
          <circle className="orb-ring r2" cx="100" cy="100" r="74" />
          <circle className="orb-ring r3" cx="100" cy="100" r="74" />
        </g>
      ) : null}

      {/* The glow tracks loudness rather than breathing on a clock. */}
      <circle
        cx="100"
        cy="100"
        r="74"
        fill={`url(#${id('brand')})`}
        filter={`url(#${id('halo')})`}
        opacity={live ? 0.35 + Math.min(level, 1) * 0.45 : 0.3}
        style={{ transition: 'opacity 90ms linear' }}
      />

      <g
        style={{
          transform: `scale(${String(scale)})`,
          transformOrigin: '100px 100px',
          // Fast enough to feel like the voice, slow enough not to jitter.
          transition: 'transform 80ms ease-out',
        }}
      >
        <circle cx="100" cy="100" r="72" fill={`url(#${id('brand')})`} />

        <g clipPath={`url(#${id('clip')})`} filter={`url(#${id('soft')})`}>
          <g className="orb-swirl">
            <ellipse
              className="orb-blob blob-a"
              cx="68"
              cy="72"
              rx="38"
              ry="30"
              style={{ fill: 'var(--brand-hot, #ff2d87)' }}
            />
            <ellipse
              className="orb-blob blob-b"
              cx="138"
              cy="128"
              rx="40"
              ry="32"
              style={{ fill: 'var(--brand-glow, #ffa234)' }}
            />
          </g>
          <g className="orb-swirl-rev">
            <ellipse
              className="orb-blob blob-c"
              cx="120"
              cy="66"
              rx="30"
              ry="26"
              style={{ fill: 'var(--brand-warm, #ff6b4a)' }}
            />
            <ellipse
              className="orb-blob blob-a"
              cx="76"
              cy="136"
              rx="28"
              ry="22"
              style={{ fill: 'var(--brand-warm, #ff6b4a)' }}
              opacity="0.9"
            />
          </g>
        </g>

        <circle cx="100" cy="100" r="72" fill={`url(#${id('depth')})`} />
        <ellipse
          cx="78"
          cy="70"
          rx="26"
          ry="18"
          fill={`url(#${id('shine')})`}
          transform="rotate(-30 78 70)"
        />
      </g>
    </svg>
  )
}
